/// <amd-module name="DistributedTaskUI/ServiceEndpoints/Editor/Utils/VisibilityHelper" />
import * as Utils_String from "VSS/Core/Util/String";
// simplified version of DistributedTask\WebPlatform\DistributedTaskControls\Components\Task\VisibilityHelper.ts

export interface IPredicateRule {
    inputName: string;
    condition: string;
    expectedValue: string;
}

export interface IVisibilityRule {
    predicateRules: (IPredicateRule | null)[];
    operator: string | null;
}

const Operator_AND: string = "&&";
const Operator_OR: string = "||";

/**
 * This class is used to help determine whether a particular object should be visible. Rules will be like '$(someValue) === true'
 * By passing in the rule and a map of values for all current inputs, determine if the rule passes or not and thus returns whether
 * a given input should be visible or not.
 */
export class VisibilityHelper {
    /**
     * Determines if a rule, such as '$(someValue) === true' passes or not.
     * @param rule the string representation of the visibility rule
     * @param getInputValue a method to get the current value of snippet input (snippetName => current value)
     */
    public static getVisibility(rule: string, getInputValue: (name: string) => any): boolean {
        const visibilityRule = this.getVisibilityRule(rule);
        if (!visibilityRule) {
            return true;
        }

        let result = visibilityRule.operator === Operator_AND;

        for (let i = 0, len = visibilityRule.predicateRules.length; i < len; i++) {
            const predicateRule = visibilityRule.predicateRules[i];
            if (!predicateRule) {
                continue;
            }

            let dependentValue = getInputValue(predicateRule.inputName);
            const predicateResult = this._getPredicateResult(predicateRule, dependentValue);
            result = this._evaluate(result, predicateResult, visibilityRule.operator);
        }

        return result;
    }

    public static getVisibilityRule(visibleRule: string): IVisibilityRule | null {
        let rule: IVisibilityRule | null = null;
        if (visibleRule) {
            if (visibleRule.indexOf(Operator_AND) !== -1) {
                let rules = visibleRule.split(Operator_AND);
                let predicateRules = rules.map(this._getPredicateRule);
                rule = {
                    operator: Operator_AND,
                    predicateRules: predicateRules
                };
            } else if (visibleRule.indexOf(Operator_OR) !== -1) {
                let rules = visibleRule.split(Operator_OR);
                let predicateRules = rules.map(this._getPredicateRule);
                rule = {
                    operator: Operator_OR,
                    predicateRules: predicateRules
                };
            } else {
                let predicateRule = this._getPredicateRule(visibleRule);
                rule = {
                    operator: null,
                    predicateRules: [predicateRule]
                };
            }
        }

        return rule;
    }

    private static _getPredicateRule(visibleRule: string): IPredicateRule | null {
        let reg = /([a-zA-Z0-9 ]+)([!=<>]+)([a-zA-Z0-9. ]+)|([a-zA-Z0-9 ]+(?=NotContains|NotEndsWith|NotStartsWith))(NotContains|NotEndsWith|NotStartsWith)([a-zA-Z0-9. ]+)|([a-zA-Z0-9 ]+(?=Contains|EndsWith|StartsWith))(Contains|EndsWith|StartsWith)([a-zA-Z0-9. ]+)/g;
        let rule: IPredicateRule | null = null;
        let matches = reg.exec(visibleRule);
        if (matches && matches.length === 10) {
            if (!!matches[1]) {
                rule = {
                    inputName: matches[1].trim(),
                    condition: matches[2].trim(),
                    expectedValue: matches[3].trim()
                };
            } else if (!!matches[4])  {
                rule = {
                    inputName: matches[4].trim(),
                    condition: matches[5].trim(),
                    expectedValue: matches[6].trim()
                };
            } else {
                rule = {
                    inputName: matches[7].trim(),
                    condition: matches[8].trim(),
                    expectedValue: matches[9].trim()
                };
            }
        }
        return rule;
    }

    private static _getPredicateResult(rule: IPredicateRule, valueToCheck: string): boolean {
        let returnValue: boolean = false;

        let valueToCheckLowerCase = valueToCheck ? valueToCheck.toString().toLowerCase() : valueToCheck;

        if (rule) {
            let expectedValue = rule.expectedValue ? rule.expectedValue.toString().toLowerCase() : rule.expectedValue;

            switch (rule.condition) {
                case "=":
                case "==":
                    returnValue = (valueToCheckLowerCase === expectedValue);
                    break;
                case "!=":
                    returnValue = (valueToCheckLowerCase !== expectedValue);
                    break;
                case "<":
                    returnValue = (valueToCheckLowerCase < expectedValue);
                    break;
                case ">":
                    returnValue = (valueToCheckLowerCase > expectedValue);
                    break;
                case "<=":
                    returnValue = (valueToCheckLowerCase <= expectedValue);
                    break;
                case ">=":
                    returnValue = (valueToCheckLowerCase >= expectedValue);
                    break;
                case "Contains":
                    returnValue = (!!valueToCheck && valueToCheck.indexOf(expectedValue) >= 0);
                    break;
                case "StartsWith":
                    returnValue = (!!valueToCheck && Utils_String.startsWith(valueToCheck, expectedValue, true));
                    break;
                case "EndsWith":
                    returnValue = (!!valueToCheck && Utils_String.endsWith(valueToCheck, expectedValue, true));
                    break;
                case "NotContains":
                    returnValue = !(valueToCheck && valueToCheck.indexOf(expectedValue) >= 0);
                    break;
                case "NotStartsWith":
                    returnValue = !(valueToCheck && Utils_String.startsWith(valueToCheck, expectedValue, true));
                    break;  
                case "NotEndsWith":
                    returnValue = !(valueToCheck && Utils_String.endsWith(valueToCheck, expectedValue, true));
                    break;    
            }
        }

        return returnValue;
    }

    private static _evaluate(expr1: boolean, expr2: boolean, operator: string | null): boolean {
        if (operator === Operator_AND) {
            return expr1 && expr2;
        } else if (operator === Operator_OR) {
            return expr1 || expr2;
        } else if (operator === null) {
            // Single condition, no operator
            return expr2;
        }

        return true;
    }
}
